import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useAuthState } from "@/modules/auth/presentation/model/useAuthState";
import {
  formatDate,
  formatDateLocal,
  formatTimeLocal,
} from "@/shared/lib/date-time";
import { AppImage } from "@/shared/ui/AppImage";
import { PresignedFileUpload } from "@/shared/ui/PresignedFileUpload";
import {
  Badge,
  Button,
  Card,
  Checkbox,
  cn,
  Input,
  Textarea,
  toast,
} from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { ArrowLeftIcon, Check, HelpCircle, Info, QrCode } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type { ChecklistDetailDTO } from "../api/client";
import {
  useChecklistDetailQuery,
  useChecklistExecutionNotesMutation,
  useChecklistItemMutation,
  useChecklistListQuery,
  useCompleteChecklistMutation,
} from "../api/queries";
import {
  formatChecklistScheduleSummary,
  labelChecklistAlertStatus,
  labelChecklistExecutionStatus,
} from "../lib/trainingLaoLabels";
import { TrainingTour } from "../tour/TrainingTour";
import {
  checklistExecutionTourSteps,
  checklistListTourSteps,
} from "../tour/trainingTourSteps";
import { useTrainingTour } from "../tour/useTrainingTour";
import { trainingRoutes } from "../trainingRoutes";
import { SchemaGapNote } from "../ui/SchemaGapNote";

type ChecklistListRow = {
  id: string;
  title: string;
  description: string | null;
  executionStatus: string;
  submissionDueAt: string | null;
  alertStatus: "none" | "due_soon" | "overdue";
  relatedSops: Array<{ id: string; title: string }>;
  relatedFlows: Array<{ id: string; title: string }>;
};

type GroupedChecklistItem = ChecklistDetailDTO["items"][number] & {
  displayTitle: string;
};

const STATUS_BADGE_VARIANT: Record<
  string,
  "default" | "secondary" | "outline" | "destructive"
> = {
  completed: "default",
  in_progress: "secondary",
  pending: "outline",
  cancelled: "destructive",
};

const CATEGORY_SPLIT = /\s*[|｜]\s*|\s+—\s+|\s+–\s+/;

function groupChecklistItems(
  items: ChecklistDetailDTO["items"],
): { category: string; items: GroupedChecklistItem[] }[] {
  const map = new Map<string, GroupedChecklistItem[]>();
  for (const item of items) {
    const parts = item.title.split(CATEGORY_SPLIT);
    const category =
      parts.length > 1 && parts[0]?.trim() ? parts[0].trim() : "ລາຍການກວດສອບ";
    const displayTitle =
      parts.length > 1 ? parts.slice(1).join(" — ").trim() : item.title;
    const row: GroupedChecklistItem = {
      ...item,
      displayTitle: displayTitle || item.title,
    };
    const list = map.get(category) ?? [];
    list.push(row);
    map.set(category, list);
  }
  return [...map.entries()].map(([category, groupItems]) => ({
    category,
    items: groupItems,
  }));
}

function ChecklistPickerPanel(props: {
  rows: ChecklistListRow[];
  filteredRows: ChecklistListRow[];
  isLoading: boolean;
  filterQuery: string;
  onFilterChange: (q: string) => void;
  onSelect: (id: string) => void;
  onBackHub: () => void;
  tour: ReturnType<typeof useTrainingTour>;
}) {
  return (
    <>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="mb-2 flex items-center gap-2">
            <h1 className="font-bold text-2xl sm:text-3xl">ລາຍການກວດສອບ</h1>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-8"
              onClick={props.tour.startTour}
              aria-label="ເລີ່ມທົດສອບການນຳທາງ"
              title="ເລີ່ມທົດສອບການນຳທາງ"
            >
              <HelpCircle className="size-4" />
            </Button>
          </div>
        </div>
        <Button
          type="button"
          variant="outline"
          className="w-full sm:mb-4 sm:w-auto"
          onClick={props.onBackHub}
        >
          <ArrowLeftIcon className="h-4 w-4" /> ກັບຄືນ
        </Button>
      </div>

      <div data-tourid="training-checklist-list-note">
        <SchemaGapNote note="ກຳນົດສົ່ງຮອບປັດຈຸບັນຄິດຈາກ schedule ຂອງ checklist ແລະເວລາເລີ່ມຮອບ (in_progress). ປ້າຍ ເກີນ / ໃກ້ຄົບ = ຄິດແບບຄຳນວນສົດ (ບໍ່ມີປະຫວັດແຈ້ງເຕືອນ). ການຜູກ SOP/Flow ມາຈາກ checklist_mapping ທີ່ກົງກັບຕຳແໜ່ງ/ພະແນກຂອງທ່ານ." />
      </div>

      <div
        className="mt-4 overflow-hidden"
        data-tourid="training-checklist-list-table"
      >
        <div
          className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
          data-tourid="training-checklist-list-search"
        >
          <label className="sr-only" htmlFor="checklist-search">
            ຄົ້ນຫາ checklist
          </label>
          <Input
            id="checklist-search"
            type="search"
            placeholder="ຄົ້ນຫາຕາມຫົວຂໍ້, ລາຍລະອຽດ ຫຼື ສະຖານະ..."
            value={props.filterQuery}
            onChange={(e) => props.onFilterChange(e.target.value)}
            className="w-full max-w-xl"
          />
          <p className="text-muted-foreground text-sm">
            ພົບ {props.filteredRows.length} ລາຍການ
          </p>
        </div>

        {props.isLoading ? (
          <p className="py-8 text-center text-muted-foreground text-sm">
            ກຳລັງໂຫລດ...
          </p>
        ) : props.rows.length === 0 ? (
          <p className="py-8 text-center text-muted-foreground text-sm">
            ບໍ່ມີລາຍການກວດສອບທີ່ມອບໝາຍໃຫ້ທ່ານ
          </p>
        ) : props.filteredRows.length === 0 ? (
          <p className="py-8 text-center text-muted-foreground text-sm">
            ບໍ່ພົບລາຍການທີ່ກົງກັບການຄົ້ນຫາ
          </p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {props.filteredRows.map((row) => (
              <button
                key={row.id}
                type="button"
                onClick={() => props.onSelect(row.id)}
                className={cn(
                  "rounded-lg border bg-card p-4 text-left transition-colors",
                  "hover:bg-accent/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  row.alertStatus === "overdue" &&
                    "border-destructive/70 bg-destructive/5",
                  row.alertStatus === "due_soon" &&
                    "border-amber-600/45 bg-amber-500/5 dark:border-amber-500/40",
                  row.alertStatus === "none" && "border-border",
                )}
              >
                <div className="flex items-start justify-between gap-2">
                  <span className="font-semibold leading-snug">
                    {row.title}
                  </span>
                  <div className="flex shrink-0 flex-col items-end gap-1">
                    {row.alertStatus !== "none" ? (
                      <Badge
                        variant={
                          row.alertStatus === "overdue"
                            ? "destructive"
                            : "outline"
                        }
                        className={
                          row.alertStatus === "due_soon"
                            ? "border-amber-600/60 text-amber-950 dark:border-amber-500/50 dark:text-amber-100"
                            : undefined
                        }
                      >
                        {labelChecklistAlertStatus(row.alertStatus)}
                      </Badge>
                    ) : null}
                    <Badge
                      variant={
                        STATUS_BADGE_VARIANT[row.executionStatus] ?? "outline"
                      }
                    >
                      {labelChecklistExecutionStatus(row.executionStatus)}
                    </Badge>
                  </div>
                </div>
                {row.description ? (
                  <p className="mt-2 line-clamp-2 text-muted-foreground text-sm">
                    {row.description}
                  </p>
                ) : null}
                {row.relatedSops.length + row.relatedFlows.length > 0 ? (
                  <p className="mt-2 line-clamp-2 text-muted-foreground text-xs">
                    {[
                      ...row.relatedSops.map((s) => `SOP: ${s.title}`),
                      ...row.relatedFlows.map((f) => `Flow: ${f.title}`),
                    ].join(" · ")}
                  </p>
                ) : null}
              </button>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

function ChecklistExecutionWorkspace(props: {
  checklistId: string;
  onClearSelection: () => void;
  tour: ReturnType<typeof useTrainingTour>;
}) {
  const navigate = useNavigate({ from: "/app/training/checklists" });
  const { user } = useAuthState();
  const { data, isLoading } = useChecklistDetailQuery(props.checklistId);
  const updateItem = useChecklistItemMutation(props.checklistId);
  const completeChecklist = useCompleteChecklistMutation(props.checklistId);
  const saveNotes = useChecklistExecutionNotesMutation(props.checklistId);

  const checklist = data;
  const disabled = checklist?.executionStatus === "completed";
  const completedCount = useMemo(
    () => checklist?.items.filter((item) => item.isCompleted).length ?? 0,
    [checklist],
  );
  const totalItems = checklist?.items.length ?? 0;
  const progressPct =
    totalItems > 0 ? Math.round((completedCount / totalItems) * 100) : 0;
  const hasItems = totalItems > 0;

  const [draftNotes, setDraftNotes] = useState("");
  useEffect(() => {
    setDraftNotes(checklist?.executionNotes ?? "");
  }, [checklist?.id, checklist?.executionNotes]);

  const grouped = useMemo(
    () => (checklist ? groupChecklistItems(checklist.items) : []),
    [checklist],
  );

  const dueIso = checklist?.submissionDueAt ?? null;
  const isOverdue = checklist?.alertStatus === "overdue";

  const handleCompleteChecklist = () => {
    completeChecklist.mutate(undefined, {
      onSuccess: () => {
        toast.success("ບັນທຶກ ແລະ ສົ່ງວຽກສຳເລັດ", {
          description: "Checklist ຖືກສົ່ງແລ້ວ",
        });
        props.onClearSelection();
      },
    });
  };

  const handleSaveDraft = () => {
    saveNotes.mutate(
      { notes: draftNotes.trim() ? draftNotes.trim() : null },
      {
        onSuccess: () => {
          toast.success("ບັນທຶກຮ່າງແລ້ວ", {
            description: "ບັນທຶກຫມາຍເຫດຮອບນີ້ແລ້ວ",
          });
        },
        onError: () => {
          toast.error("ບໍ່ສາມາດບັນທຶກໄດ້", {
            description: "ລອງໃໝ່ ຫຼື ສົ່ງ checklist ກ່ອນ",
          });
        },
      },
    );
  };

  const handleStartNewRound = () => {
    if (!checklist || checklist.items.length === 0) return;
    const firstItem = checklist.items[0];
    if (!firstItem) return;
    updateItem.mutate(
      {
        itemId: firstItem.id,
        isCompleted: false,
        notes: null,
        photoUrl: null,
      },
      {
        onSuccess: () => {
          toast.success("ເລີ່ມຮອບໃໝ່ແລ້ວ", {
            description: "ທ່ານສາມາດກວດ Checklist ຮອບໃໝ່ໄດ້",
          });
        },
      },
    );
  };

  if (isLoading) {
    return (
      <p className="py-12 text-center text-muted-foreground">ກຳລັງໂຫລດ...</p>
    );
  }

  if (!checklist) {
    return (
      <div className="space-y-4 py-8">
        <p className="text-muted-foreground">ບໍ່ພົບຂໍ້ມູນ checklist</p>
        <Button
          type="button"
          variant="outline"
          onClick={props.onClearSelection}
        >
          <ArrowLeftIcon className="mr-2 size-4" /> ກັບໄປລາຍການ
        </Button>
      </div>
    );
  }

  const sessionLabel = formatDateLocal(
    checklist.executionStartedAt ?? new Date(),
  );
  const clockLabel = formatTimeLocal(new Date());

  return (
    <div className="flex min-h-[calc(100vh-8rem)] flex-col gap-0">
      <div className="mb-6 flex flex-col gap-4 border-border border-b pb-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="-ms-2 gap-1 px-2"
              onClick={props.onClearSelection}
            >
              <ArrowLeftIcon className="size-4" />
              ລາຍການ
            </Button>
            <span className="text-muted-foreground">/</span>
            <h1
              className="font-bold text-2xl tracking-tight sm:text-3xl"
              data-tourid="training-checklist-detail-title"
            >
              ລາຍການກວດສອບ
            </h1>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-8 shrink-0"
              onClick={props.tour.startTour}
              aria-label="ເລີ່ມທົດສອບການນຳທາງ"
              title="ເລີ່ມທົດສອບການນຳທາງ"
            >
              <HelpCircle className="size-4" />
            </Button>
          </div>
          <p className="text-muted-foreground text-sm">{sessionLabel}</p>
          <p className="font-medium text-lg">{checklist.title}</p>
          {checklist.description ? (
            <p className="max-w-3xl text-muted-foreground text-sm">
              {checklist.description}
            </p>
          ) : null}
        </div>
        <div
          className="flex flex-wrap gap-2 text-muted-foreground text-sm"
          data-tourid="training-checklist-detail-meta"
        >
          {checklist.alertStatus !== "none" ? (
            <Badge
              variant={
                checklist.alertStatus === "overdue" ? "destructive" : "outline"
              }
              className={
                checklist.alertStatus === "due_soon"
                  ? "border-amber-600/60 text-amber-950 dark:border-amber-500/50 dark:text-amber-100"
                  : undefined
              }
            >
              {labelChecklistAlertStatus(checklist.alertStatus)}
            </Badge>
          ) : null}
          <Badge variant="secondary">
            {labelChecklistExecutionStatus(checklist.executionStatus)}
          </Badge>
          <span className="self-center">
            {formatChecklistScheduleSummary({
              scheduleType: checklist.scheduleType,
              scheduleFrequency: checklist.scheduleFrequency,
              scheduleDayOfWeek: checklist.scheduleDayOfWeek,
              scheduleDayOfMonth: checklist.scheduleDayOfMonth,
            })}
          </span>
          {checklist.executionStatus === "in_progress" && dueIso ? (
            <span
              className={cn(
                "self-center",
                isOverdue && "font-medium text-destructive",
                checklist.alertStatus === "due_soon" &&
                  "font-medium text-amber-800 dark:text-amber-200",
              )}
            >
              ກຳນົດສົ່ງ: {formatDate(dueIso)}
              {isOverdue ? " · ເກີນກຳນົດ" : ""}
              {checklist.alertStatus === "due_soon" && !isOverdue
                ? " · ໃກ້ຄົບກຳນົດ"
                : ""}
            </span>
          ) : null}
        </div>
      </div>

      <div
        className="grid flex-1 gap-6 lg:grid-cols-[1fr_min(100%,380px)] xl:gap-8"
        data-tourid="training-checklist-detail-items"
      >
        <div className="min-w-0 space-y-8 pb-4">
          {(checklist.relatedSops.length > 0 ||
            checklist.relatedFlows.length > 0) && (
            <div className="rounded-lg border border-border bg-muted/20 p-3 text-sm">
              <p className="mb-2 font-medium text-muted-foreground">
                ກ່ຽວຂ້ອງກັບຂັ້ນຕອນ / ແຜນ Flow
              </p>
              <ul className="flex flex-wrap gap-x-4 gap-y-1">
                {checklist.relatedSops.map((s) => (
                  <li key={`sop-${s.id}`}>
                    <Button
                      type="button"
                      variant="link"
                      className="h-auto min-h-0 p-0 font-normal"
                      onClick={() =>
                        navigate({
                          to: trainingRoutes.sopDetail,
                          params: { id: s.id },
                        })
                      }
                    >
                      SOP: {s.title}
                    </Button>
                  </li>
                ))}
                {checklist.relatedFlows.map((f) => (
                  <li key={`flow-${f.id}`}>
                    <Button
                      type="button"
                      variant="link"
                      className="h-auto min-h-0 p-0 font-normal"
                      onClick={() =>
                        navigate({
                          to: trainingRoutes.flowDetail,
                          params: { id: f.id },
                        })
                      }
                    >
                      Flow: {f.title}
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {grouped.map((group) => (
            <section key={group.category}>
              <h2 className="mb-3 font-medium text-muted-foreground text-xs uppercase tracking-wide">
                {group.category}
              </h2>
              <div className="space-y-2">
                {group.items.map((item) => (
                  <div
                    key={item.id}
                    className={cn(
                      "flex gap-3 rounded-lg border border-border bg-card p-4 transition-colors",
                      item.isCompleted && "bg-muted/30",
                    )}
                  >
                    <Checkbox
                      className="mt-1 size-5 shrink-0 rounded-sm"
                      id={`checklist-item-${item.id}`}
                      checked={item.isCompleted}
                      disabled={disabled}
                      onCheckedChange={(value) =>
                        updateItem.mutate({
                          itemId: item.id,
                          isCompleted: Boolean(value),
                          notes: item.notes,
                          photoUrl: item.photoUrl,
                        })
                      }
                    />
                    <div className="min-w-0 flex-1">
                      <label
                        htmlFor={`checklist-item-${item.id}`}
                        className="cursor-pointer font-semibold leading-snug"
                      >
                        {item.displayTitle}
                      </label>
                      {item.description ? (
                        <p className="mt-1 text-muted-foreground text-sm leading-relaxed">
                          {item.description}
                        </p>
                      ) : null}
                      {item.allowNotes ? (
                        <Input
                          className="mt-3 w-full max-w-lg"
                          disabled={disabled}
                          defaultValue={item.notes ?? ""}
                          placeholder="ໝາຍເຫດລາຍການນີ້"
                          onBlur={(event) =>
                            updateItem.mutate({
                              itemId: item.id,
                              isCompleted: item.isCompleted,
                              notes: event.target.value,
                              photoUrl: item.photoUrl,
                            })
                          }
                        />
                      ) : null}
                      {checklist.requirePhoto ? (
                        <div className="mt-3 max-w-lg space-y-2">
                          <PresignedFileUpload
                            keyPrefix="uploads/checklists"
                            accept="image/*"
                            buttonLabel="ອັບໂຫຼດຮູບຫຼັກຖານ"
                            hint={item.photoUrl ? "ມີຮູບແລ້ວ" : "ຍັງບໍ່ມີຮູບຫຼັກຖານ"}
                            onSuccess={(photoKey) =>
                              updateItem.mutate({
                                itemId: item.id,
                                isCompleted: item.isCompleted,
                                notes: item.notes,
                                photoUrl: photoKey,
                              })
                            }
                          />
                          {item.photoUrl ? (
                            <AppImage
                              src={item.photoUrl}
                              alt=""
                              className="h-28 w-full max-w-xs rounded-md border object-cover"
                              fit="cover"
                            />
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                    <div className="flex shrink-0 flex-col gap-1 self-start pt-0.5">
                      {item.isRequired ? (
                        <span title="ຈຳເປັນ">
                          <Info className="size-4 text-muted-foreground" />
                        </span>
                      ) : null}
                      {item.isCompleted ? (
                        <Check className="size-4 text-primary" />
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>

        <aside className="lg:border-border lg:border-s lg:ps-6 xl:ps-8">
          <div className="sticky top-4 space-y-5">
            <Card className="gap-2 border-border bg-muted/15 p-4 shadow-none">
              <p className="font-medium text-sm">ຄວາມຄືບໜ້າ</p>
              <p className="mt-1 font-bold text-3xl tabular-nums">
                {progressPct}%
              </p>
              <div className="mt-3 h-2 overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full rounded-full bg-primary transition-all"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
              <p className="mt-2 text-muted-foreground text-xs">
                ສຳເລັດ {completedCount} ຈາກ {totalItems} ລາຍການ
              </p>
            </Card>

            <div>
              <label
                className="mb-2 block font-medium text-sm"
                htmlFor="checklist-exec-notes"
              >
                ຄຳອະທິບາຍ / ໝາຍເຫດຮອບນີ້
              </label>
              <Textarea
                id="checklist-exec-notes"
                disabled={disabled}
                value={draftNotes}
                onChange={(e) => setDraftNotes(e.target.value)}
                placeholder="ບັນທຶກສິ່ງທີ່ຕ້ອງລະບຸຕໍ່ຮອບກວດນີ້..."
                className="min-h-[120px] resize-y border-border bg-background"
              />
            </div>

            <div
              className="flex flex-col gap-2"
              data-tourid="training-checklist-detail-actions"
            >
              <Button
                type="button"
                className="w-full"
                disabled={disabled || completeChecklist.isPending}
                onClick={handleCompleteChecklist}
              >
                ບັນທຶກ ແລະ ສົ່ງວຽກ
              </Button>
              <Button
                type="button"
                variant="outline"
                className="w-full border-border"
                disabled={disabled || saveNotes.isPending}
                onClick={handleSaveDraft}
              >
                ບັນທຶກຮ່າງໄວ້ກ່ອນ
              </Button>
              {disabled && hasItems ? (
                <Button
                  type="button"
                  variant="secondary"
                  className="w-full"
                  disabled={updateItem.isPending}
                  onClick={handleStartNewRound}
                >
                  ເລີ່ມຮອບໃໝ່
                </Button>
              ) : null}
            </div>
          </div>
        </aside>
      </div>

      <footer className="mt-auto flex flex-wrap items-center justify-between gap-3 border-border border-t bg-background/95 py-3 text-muted-foreground text-sm backdrop-blur supports-backdrop-filter:bg-background/80">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          <span className="font-medium text-foreground">
            {user?.name ?? "—"}
          </span>
          <span>{clockLabel}</span>
          <span className="max-w-[200px] truncate sm:max-w-none">
            {user?.email ?? "—"}
          </span>
        </div>
        <QrCode className="size-8 shrink-0 text-muted-foreground" aria-hidden />
      </footer>
    </div>
  );
}

export function ChecklistListPage() {
  const listTour = useTrainingTour("training-checklist-list-tour-completed");
  const execTour = useTrainingTour(
    "training-checklist-execution-tour-completed",
  );
  const navigate = useNavigate({ from: "/app/training/checklists" });
  const routeSearch = useSearch({ from: "/app/training/checklists" });
  const checklistId = routeSearch.checklistId;

  const sortBy = routeSearch.sortBy ?? "title";
  const sortOrder: "asc" | "desc" =
    routeSearch.sortOrder === "desc" ? "desc" : "asc";
  const { data, isLoading } = useChecklistListQuery(sortBy, sortOrder);
  const rows = (data?.data ?? []) as ChecklistListRow[];

  const [filterQuery, setFilterQuery] = useState("");

  const filteredRows = useMemo(() => {
    const q = filterQuery.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) => {
      const title = row.title.toLowerCase();
      const desc = (row.description ?? "").toLowerCase();
      const statusLao = labelChecklistExecutionStatus(
        row.executionStatus,
      ).toLowerCase();
      const alertLao = labelChecklistAlertStatus(row.alertStatus).toLowerCase();
      const rel = [
        ...row.relatedSops.map((s) => s.title),
        ...row.relatedFlows.map((f) => f.title),
      ]
        .join(" ")
        .toLowerCase();
      return (
        title.includes(q) ||
        desc.includes(q) ||
        statusLao.includes(q) ||
        row.executionStatus.toLowerCase().includes(q) ||
        alertLao.includes(q) ||
        rel.includes(q)
      );
    });
  }, [rows, filterQuery]);

  const clearChecklistSelection = () => {
    navigate({
      search: {
        sortBy: routeSearch.sortBy,
        sortOrder: routeSearch.sortOrder,
        checklistId: undefined,
      },
    });
  };

  const selectChecklist = (id: string) => {
    navigate({
      search: {
        ...routeSearch,
        checklistId: id,
      },
    });
  };

  return (
    <>
      <Header />
      <Main>
        <div className="mx-auto max-w-[1600px]">
          {!checklistId ? (
            <ChecklistPickerPanel
              rows={rows}
              filteredRows={filteredRows}
              isLoading={isLoading}
              filterQuery={filterQuery}
              onFilterChange={setFilterQuery}
              onSelect={selectChecklist}
              onBackHub={() => navigate({ to: trainingRoutes.exitToAppHome })}
              tour={listTour}
            />
          ) : (
            <ChecklistExecutionWorkspace
              checklistId={checklistId}
              onClearSelection={clearChecklistSelection}
              tour={execTour}
            />
          )}
        </div>

        {!checklistId ? (
          <TrainingTour
            steps={checklistListTourSteps}
            run={listTour.run}
            onCallback={listTour.handleJoyrideCallback}
          />
        ) : (
          <TrainingTour
            steps={checklistExecutionTourSteps}
            run={execTour.run}
            onCallback={execTour.handleJoyrideCallback}
          />
        )}
      </Main>
    </>
  );
}
